/**
 * Pure helpers for a customer's address book (`CourierUser.addresses`).
 *
 * Kept free of React so both portals can share them: admin imports deep
 * (`@shared/components/addressBook`), customer imports from the barrel
 * (`@shared/components`) — see the alias note in the repo docs. Because this
 * module is pure data it is also spread back in via `vi.importActual` in the
 * customer app's global `@shared/components` mock, the same way `currencies`
 * and `countries` are.
 *
 * The backend mirror of this logic lives in
 * `apps/legacy-api/server/utils/customer-address.js` and is the authority —
 * these helpers keep the form in the same shape so a save is a no-op reshuffle.
 */

/** Structurally compatible with the SDK's `Address` model. */
export interface CustomerAddress {
  id?: number;
  name?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  isPrimary?: boolean;
  coordinates?: { lat: number; lng: number };
}

export const ADDRESS_LABEL_PRESETS = ['Home', 'Work', 'Delivery', 'Billing'];

/**
 * Fields that count as real content. `country` is deliberately excluded: the
 * backend seeds new users with `{ country: 'Jamaica' }` and nothing else, and
 * those placeholders should not read as addresses the user actually entered.
 */
const CONTENT_FIELDS: Array<keyof CustomerAddress> = [
  'street1',
  'street2',
  'city',
  'state',
  'zip',
];

export const emptyAddress = (): CustomerAddress => ({
  name: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  isPrimary: false,
});

/**
 * True when nothing has been entered. A labelled-but-empty entry is *not*
 * blank: it is a card the user deliberately added and is still filling in.
 */
export const isBlankAddress = (address?: CustomerAddress | null): boolean => {
  if (!address) return true;
  if (address.name && String(address.name).trim()) return false;
  return !CONTENT_FIELDS.some((field) => {
    const value = address[field];
    return value !== null && value !== undefined && String(value).trim() !== '';
  });
};

/** The one-line text shown on a collapsed address card. */
export const formatAddressSummary = (address?: CustomerAddress | null): string => {
  if (!address) return '';
  return [address.street1, address.street2, address.city, address.state]
    .filter((part) => part && String(part).trim())
    .join(', ');
};

/** Returns the list with `isPrimary` on `index` only. Out-of-range is a no-op. */
export const withPrimaryAt = (
  addresses: CustomerAddress[],
  index: number
): CustomerAddress[] => {
  if (index < 0 || index >= addresses.length) return addresses;
  return addresses.map((address, i) => ({ ...address, isPrimary: i === index }));
};

/**
 * Removes the entry at `index`. If that entry was the primary, the flag moves
 * to the first survivor. The form always keeps at least one card, so removing
 * the last entry leaves a fresh blank one rather than an empty list.
 */
export const removeAddressAt = (
  addresses: CustomerAddress[],
  index: number
): CustomerAddress[] => {
  const remaining = addresses.filter((_, i) => i !== index);
  if (!remaining.length) return [{ ...emptyAddress(), isPrimary: true }];
  return remaining.some((address) => address.isPrimary)
    ? remaining
    : withPrimaryAt(remaining, 0);
};

/**
 * Guarantees exactly one primary — the flagged entry if there is one, else the
 * first. Mirrors `normalizeAddresses` on the backend so hydrating a record and
 * saving it straight back changes nothing.
 */
export const ensureOnePrimary = (addresses: CustomerAddress[]): CustomerAddress[] => {
  if (!addresses || !addresses.length) return [{ ...emptyAddress(), isPrimary: true }];
  const flagged = addresses.findIndex((address) => address.isPrimary);
  return withPrimaryAt(addresses, flagged === -1 ? 0 : flagged);
};

/**
 * The exact array to send to the API: blank cards dropped, then exactly one
 * primary, with every field normalized and each existing `id` preserved so the
 * backend updates rows in place rather than replacing them.
 *
 * Blanks are dropped *before* choosing the primary — filtering afterwards can
 * remove the very entry that was flagged and leave the book with none.
 * An `id` or `coordinates` is omitted entirely rather than sent as undefined.
 */
export const toSavePayload = (addresses: CustomerAddress[]): CustomerAddress[] => {
  const filled = (addresses || []).filter((address) => !isBlankAddress(address));
  if (!filled.length) return [];
  return ensureOnePrimary(filled).map((address) => ({
    ...(address.id ? { id: address.id } : {}),
    name: address.name || '',
    street1: address.street1 || '',
    street2: address.street2 || '',
    city: address.city || '',
    state: address.state || '',
    zip: address.zip || '',
    country: address.country || '',
    isPrimary: Boolean(address.isPrimary),
    ...(address.coordinates ? { coordinates: address.coordinates } : {}),
  }));
};
